import { apiUrl } from '@/shared/config';

/**
 * The HTML of a signature on this side (contract v10.57, ADR 0054 of the back): what goes
 * into the editor, and how Outlook's `.htm` is read with its images.
 *
 * The back cleans every signature when it is saved. This cleans what arrives in the
 * editor before it is painted (a paste, an imported `.htm`), because the editor is a
 * `contentEditable` in the page: an `onerror` or a `<style>` would run or restyle the
 * application. The parse is a `DOMParser` document, which runs nothing and loads nothing.
 */

/** What the back accepts inside a signature (`IMAGE_TYPES`). */
export const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif'] as const;

/** The same ceiling as the back's, said before sending. */
export const MAX_IMAGE_BYTES = 1_000_000;

export const IMAGE_PATH = '/api/profile/signatures/images/';

/**
 * How a signature is painted here, so it reads as it will arrive: Outlook's font for what
 * says none, and inside it the browser's own styles instead of this application's reset
 * (a `<p>` with its margin, a list with its bullets), as a mail client shows them. An
 * inline `style` still wins over `revert`.
 */
export const MAIL_LOOK =
  'bg-white text-[11pt] text-black [font-family:Calibri,Arial,sans-serif] [&_*]:[all:revert]';

const DROPPED = [
  'script',
  'style',
  'head',
  'title',
  'meta',
  'link',
  'base',
  'iframe',
  'frame',
  'frameset',
  'object',
  'embed',
  'applet',
  'form',
  'input',
  'select',
  'textarea',
  'button',
  'noscript',
  'svg',
  'math',
  'template',
  'audio',
  'video',
  'canvas',
  'xml',
];

const URL_ATTRIBUTES = [
  'href',
  'src',
  'action',
  'formaction',
  'background',
  'poster',
  'xlink:href',
];

/** Paints what arrives safely in the editor: nothing that runs, nothing of the page's. */
export function cleanForEditor(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  // Outlook gives `p.MsoNormal` no margin in a style sheet that goes; said in the paragraph.
  for (const element of Array.from(doc.querySelectorAll<HTMLElement>('.MsoNormal'))) {
    const style = element.getAttribute('style') ?? '';
    if (!/margin/i.test(style)) element.setAttribute('style', `margin:0;${style}`);
  }
  for (const element of Array.from(doc.querySelectorAll(DROPPED.join(',')))) element.remove();
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_COMMENT);
  const comments: Node[] = [];
  while (walker.nextNode()) comments.push(walker.currentNode);
  for (const comment of comments) comment.parentNode?.removeChild(comment);
  for (const element of Array.from(doc.body.querySelectorAll('*'))) {
    for (const attribute of Array.from(element.attributes)) {
      const name = attribute.name.toLowerCase();
      const value = attribute.value.trim().toLowerCase();
      // `class` and `id` are the application's vocabulary: an Outlook `class="field"`
      // would take this page's look, and an `id` could clash with one of its own.
      if (name.startsWith('on') || name === 'class' || name === 'id') {
        element.removeAttribute(attribute.name);
      } else if (
        URL_ATTRIBUTES.includes(name) &&
        (value.startsWith('javascript:') || value.startsWith('vbscript:'))
      ) {
        element.removeAttribute(attribute.name);
      }
    }
  }
  hintsAsStyles(doc.body);
  return doc.body.innerHTML.trim();
}

/** `<font size>` in CSS, as browsers read it. */
const FONT_SIZES = ['', 'x-small', 'small', 'medium', 'large', 'x-large', 'xx-large', 'xxx-large'];

function lengthOf(value: string): string | null {
  const trimmed = value.trim();
  if (/^\d+(\.\d+)?$/.test(trimmed)) return `${trimmed}px`;
  if (/^\d+(\.\d+)?%$/.test(trimmed)) return trimmed;
  return null;
}

/**
 * Outlook lays a signature out with attributes (`<img width>`, `<td valign>`, `<font
 * color>`, `cellpadding`). `MAIL_LOOK` takes this page's styles off with `all: revert`, and
 * that takes the attributes off too: a CSS `revert` goes back past every author rule, and an
 * attribute counts as one. Said again as an inline style, which wins over `revert`, the
 * signature looks here as it will in the message. The attributes stay, for the clients that
 * read only them.
 */
function hintsAsStyles(root: HTMLElement): void {
  const add = (element: HTMLElement, property: string, value: string | null): void => {
    if (value && !element.style.getPropertyValue(property)) {
      element.style.setProperty(property, value);
    }
  };
  for (const element of Array.from(root.querySelectorAll<HTMLElement>('*'))) {
    const tag = element.tagName.toLowerCase();
    const attribute = (name: string): string | null => element.getAttribute(name);
    if (['img', 'table', 'td', 'th'].includes(tag)) {
      add(element, 'width', lengthOf(attribute('width') ?? ''));
      add(element, 'height', lengthOf(attribute('height') ?? ''));
    }
    add(element, 'background-color', attribute('bgcolor'));
    const align = attribute('align')?.toLowerCase();
    if (align && ['left', 'right', 'center', 'justify'].includes(align)) {
      // On an image or a table, `align` floats or centres the box, not its text.
      if (tag !== 'img' && tag !== 'table') add(element, 'text-align', align);
    }
    const valign = attribute('valign')?.toLowerCase();
    if (valign && ['top', 'middle', 'bottom', 'baseline'].includes(valign)) {
      add(element, 'vertical-align', valign);
    }
    if (tag === 'font') {
      add(element, 'color', attribute('color'));
      add(element, 'font-family', attribute('face'));
      add(element, 'font-size', FONT_SIZES[Number(attribute('size'))] ?? null);
    }
    if (tag === 'table') {
      const spacing = lengthOf(attribute('cellspacing') ?? '');
      add(element, 'border-spacing', spacing);
      const padding = lengthOf(attribute('cellpadding') ?? '');
      if (padding) {
        const table = element as HTMLTableElement;
        for (const row of Array.from(table.rows)) {
          for (const cell of Array.from(row.cells)) add(cell, 'padding', padding);
        }
      }
    }
  }
}

/** A saved signature's images, from where the back says (`/api/...`) to where the API
 * is: the same host in production, another one in development. */
export function forScreen(html: string): string {
  if (!html) return '';
  return html.split(`src="${IMAGE_PATH}`).join(`src="${apiUrl}${IMAGE_PATH}`);
}

/** Whether the signature says or shows anything: an empty editor is not one. */
export function isEmpty(html: string): boolean {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return !doc.body.textContent?.replace(/\u00a0/g, ' ').trim() && !doc.querySelector('img');
}

export function readAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error ?? new Error('No se pudo leer el fichero.'));
    reader.readAsDataURL(file);
  });
}

function readAsArrayBuffer(file: Blob): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve(reader.result instanceof ArrayBuffer ? reader.result : new ArrayBuffer(0));
    reader.onerror = () => reject(reader.error ?? new Error('No se pudo leer el fichero.'));
    reader.readAsArrayBuffer(file);
  });
}

/** Why an image cannot go into a signature, in a sentence; null if it can. */
export function imageProblem(file: { type: string; size: number }): string | null {
  if (!(IMAGE_TYPES as readonly string[]).includes(file.type)) {
    return 'Solo imágenes PNG, JPEG o GIF: son las que se ven en cualquier programa de correo.';
  }
  if (file.size > MAX_IMAGE_BYTES) return 'La imagen pasa de 1 MB. Usa una más pequeña.';
  return null;
}

/** The text of a `.htm` as Outlook wrote it: usually `windows-1252`, said in its `<meta>`. */
export async function readHtm(file: Blob): Promise<string> {
  const bytes = new Uint8Array(await readAsArrayBuffer(file));
  const head = new TextDecoder('latin1').decode(bytes.slice(0, 2048));
  const declared = /charset\s*=\s*["']?([\w-]+)/i.exec(head)?.[1]?.toLowerCase();
  try {
    return new TextDecoder(declared ?? 'utf-8').decode(bytes);
  } catch {
    return new TextDecoder('utf-8').decode(bytes);
  }
}

/** A file picked from a folder: its path inside it, as `webkitdirectory` gives it. */
export interface PickedFile {
  file: File;
  path: string;
}

export function picked(files: FileList | File[]): PickedFile[] {
  return Array.from(files).map((file) => ({
    file,
    path: (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name,
  }));
}

/** The signatures in what was picked: each `.htm`, by its name without the extension. */
export function outlookSignatures(files: PickedFile[]): PickedFile[] {
  return files.filter((each) => /\.html?$/i.test(each.path));
}

export function signatureName(each: PickedFile): string {
  return each.file.name.replace(/\.html?$/i, '');
}

function basename(path: string): string {
  const last = path.replace(/\\/g, '/').split('/').pop() ?? path;
  try {
    return decodeURIComponent(last.split('?')[0] ?? last).toLowerCase();
  } catch {
    return last.toLowerCase();
  }
}

/**
 * One Outlook signature with its images inside: the `.htm` and, beside it, the folder
 * Outlook keeps its images in (`Nombre_archivos` in Spanish, `Nombre_files` in English).
 * Each `<img>` that points there gets the image as `data:`; one that is not there stays
 * as it was and the back removes it, saying so.
 */
export async function importOutlook(
  htm: PickedFile,
  files: PickedFile[],
): Promise<{ html: string; missing: number }> {
  const name = signatureName(htm).toLowerCase();
  const folder = htm.path.replace(/\\/g, '/').split('/').slice(0, -1).join('/');
  const images = new Map<string, File>();
  for (const each of files) {
    const path = each.path.replace(/\\/g, '/');
    const parts = path.split('/');
    const parent = (parts[parts.length - 2] ?? '').toLowerCase();
    const inFolder = parts.slice(0, -2).join('/') === folder;
    if (inFolder && parent.startsWith(`${name}_`) && each.file.type.startsWith('image/')) {
      images.set(basename(path), each.file);
    }
  }
  const doc = new DOMParser().parseFromString(await readHtm(htm.file), 'text/html');
  let missing = 0;
  for (const img of Array.from(doc.querySelectorAll('img'))) {
    const src = img.getAttribute('src') ?? '';
    if (/^(data|https?):/i.test(src)) continue;
    const file = images.get(basename(src));
    if (file && !imageProblem(file)) {
      img.setAttribute('src', await readAsDataUrl(file));
    } else {
      missing += 1;
      img.remove();
    }
  }
  return { html: cleanForEditor(doc.documentElement.outerHTML), missing };
}
